import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Search, MessageCircle, Mail, BarChart3, ChevronDown, ChevronRight } from "lucide-react";

interface ChatSession {
  session_id: string;
  started_at: string;
  message_count: number;
  preview: string;
  page_context: string;
  messages: { role: string; content: string; created_at: string }[];
}

interface Contact {
  id: string;
  nombre: string;
  email: string;
  empresa: string | null;
  mensaje: string;
  source: string;
  created_at: string;
}

interface Report {
  id: string;
  client_name: string;
  client_email: string;
  client_company: string;
  business_type: string;
  main_problem: string;
  created_at: string;
}

export default function Admin() {
  const [adminKey, setAdminKey] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [error, setError] = useState("");

  const fetchData = async (type: string, key: string) => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const url = `https://${projectId}.supabase.co/functions/v1/admin-data?type=${type}`;
    const res = await fetch(url, {
      headers: { "x-admin-key": key },
    });

    if (!res.ok) {
      throw new Error(res.status === 401 ? "Clave incorrecta" : "Error al cargar datos");
    }

    return res.json();
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError("");
    try {
      const data = await fetchData("chat", adminKey);
      setSessions(data.sessions || []);
      setIsAuthenticated(true);

      // Load other tabs
      const [contactsData, reportsData] = await Promise.all([
        fetchData("contacts", adminKey),
        fetchData("reports", adminKey),
      ]);
      setContacts(contactsData.contacts || []);
      setReports(reportsData.reports || []);
    } catch (err: any) {
      setError(err.message || "Error de autenticación");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredSessions = sessions.filter(
    (s) =>
      !search ||
      s.preview.toLowerCase().includes(search.toLowerCase()) ||
      s.messages.some((m) => m.content.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredContacts = contacts.filter(
    (c) =>
      !search ||
      c.nombre.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.mensaje.toLowerCase().includes(search.toLowerCase())
  );

  const filteredReports = reports.filter(
    (r) =>
      !search ||
      r.client_name.toLowerCase().includes(search.toLowerCase()) ||
      r.client_email.toLowerCase().includes(search.toLowerCase()) ||
      r.client_company.toLowerCase().includes(search.toLowerCase())
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-semibold">Panel Admin</h1>
            <p className="text-sm text-muted-foreground">Introduce la clave de acceso</p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
            className="space-y-4"
          >
            <Input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="Clave de admin"
              className="h-12"
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full h-12" disabled={isLoading || !adminKey}>
              {isLoading ? "Verificando..." : "Entrar"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 px-4 pb-12">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Panel Admin</h1>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="pl-9"
            />
          </div>
        </div>

        <Tabs defaultValue="chat">
          <TabsList>
            <TabsTrigger value="chat" className="gap-1.5">
              <MessageCircle className="w-4 h-4" /> Chat ({sessions.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-1.5">
              <Mail className="w-4 h-4" /> Contactos ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-1.5">
              <BarChart3 className="w-4 h-4" /> Informes ({reports.length})
            </TabsTrigger>
          </TabsList>

          {/* CHAT TAB */}
          <TabsContent value="chat">
            <div className="space-y-2">
              {filteredSessions.length === 0 && (
                <p className="text-muted-foreground text-center py-12">No hay conversaciones aún</p>
              )}
              {filteredSessions.map((session) => (
                <div key={session.session_id} className="border rounded-lg">
                  <button
                    onClick={() =>
                      setExpandedSession(expandedSession === session.session_id ? null : session.session_id)
                    }
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/50 transition-colors"
                  >
                    {expandedSession === session.session_id ? (
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{session.preview || "Sin preview"}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(session.started_at)} · {session.message_count} msgs · {session.page_context}
                      </p>
                    </div>
                  </button>
                  {expandedSession === session.session_id && (
                    <div className="border-t p-4 space-y-3 bg-muted/20">
                      {session.messages.map((msg, i) => (
                        <div
                          key={i}
                          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-xl px-4 py-2 text-sm ${
                              msg.role === "user"
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-foreground"
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            <p className="text-[10px] opacity-60 mt-1">{formatDate(msg.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CONTACTS TAB */}
          <TabsContent value="contacts">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Mensaje</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                        No hay contactos aún
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredContacts.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.nombre}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.empresa || "—"}</TableCell>
                      <TableCell className="max-w-xs truncate">{c.mensaje}</TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded-full bg-muted">
                          {c.source === "quick_contact" ? "Quick" : "Contacto"}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(c.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* REPORTS TAB */}
          <TabsContent value="reports">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Tipo negocio</TableHead>
                    <TableHead>Problema</TableHead>
                    <TableHead>Fecha</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-12">
                        No hay informes aún
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredReports.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.client_name}</TableCell>
                      <TableCell>{r.client_email}</TableCell>
                      <TableCell>{r.client_company}</TableCell>
                      <TableCell>{r.business_type}</TableCell>
                      <TableCell className="max-w-xs truncate">{r.main_problem}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(r.created_at || "")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
